import { Mutation, Typenames } from '@varo/graphql-schema-types';
import { useCallback } from 'react';
import { UpdateBestAction } from 'services/graphql/mutations/UpdateBestAction';
import useCancellableMutation from 'shared/hooks/useCancellableMutation/useCancellableMutation';
import { logErrorWithContext } from 'shared/utils/sentry/sentry';
import {
  ErrorSeverity,
  FeatureArea,
  ResponsibleTeam,
} from 'shared/utils/sentry/sentry.constants';
import { CombinedError } from 'urql';

export interface UpdateBestActionData {
  updateBestActionLoading: boolean;
  error?: CombinedError;
  executeUpdateBestAction: (
    wizardId?: string,
    optionId?: string,
  ) => Promise<boolean | undefined>;
}

const useUpdateBestAction = (): UpdateBestActionData => {
  const [{ fetching, error }, executeCancellableMutation] =
    useCancellableMutation<Mutation>(UpdateBestAction);

  const executeUpdateBestAction = useCallback(
    async (
      wizardId?: string,
      optionId?: string,
    ): Promise<boolean | undefined> => {
      try {
        const response = await executeCancellableMutation({
          input: { id: optionId ?? wizardId },
        });
        return (
          response?.data?.updateBestAction?.__typename ===
          Typenames.UpdateBestActionSuccess
        );
      } catch (error) {
        logErrorWithContext(error, {
          featureArea: FeatureArea.BELIEVE,
          responsibleTeam: ResponsibleTeam.CUSTOMER_ACQUISITION,
          severity: ErrorSeverity.MEDIUM,
        });
        return false;
      }
    },
    [executeCancellableMutation],
  );

  return {
    updateBestActionLoading: fetching,
    error: error,
    executeUpdateBestAction: executeUpdateBestAction,
  };
};

export default useUpdateBestAction;
